import React, { useState } from 'react';
import {
  Container,
  Box,
  makeStyles,
  withStyles,
  AppBar,
  Toolbar,
  Button,
  Grid,
  Typography,
  InputBase,
  Divider,
  FormControl,
  Select,
  FormHelperText,
} from '@material-ui/core';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { primary } from '../../theme';

import Results from './Results';

const CustomInput = withStyles((theme) => ({
  root: {
    'label + &': {
      marginTop: theme.spacing(3),
    },
  },
  input: {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #ced4da',
    fontSize: 16,
    padding: '10px 26px 10px 12px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    // Use the system font instead of the default Roboto font.
    fontFamily: 'Open Sans, sans-serif',
    '&:focus': {
      borderRadius: 4,
    },
  },
}))(InputBase);

const useStyles = makeStyles((theme) => ({
  appBar: {
    fontFamily: 'Open Sans, sans-serif',
    top: 'auto',
    bottom: 0,
  },
  grow: {
    flexGrow: 1,
  },
  paper: {
    padding: 0,
    textAlign: 'center',
  },
  nextButton: {
    minWidth: '100%',
    fontFamily: 'Open Sans, sans-serif',
  },
  pageTitle: {
    fontSize: 20,
    fontWeight: 700,
    marginTop: 50,
    marginBottom: 20,
    color: primary.purple,
    fontFamily: 'Open Sans, sans-serif',
  },
  pageSubhead: {
    fontSize: 16,
    marginBottom: 20,
    fontFamily: 'Open Sans, sans-serif',
  },
  pageCopy: {
    fontSize: 14,
    fontFamily: 'Open Sans, sans-serif',
    paddingTop: 10,
    minHeight: 46,
    [theme.breakpoints.down('lg')]: {
      paddingTop: 15,
      minHeight: 80,
    },
    [theme.breakpoints.down('md')]: {
      paddingTop: 20,
      minHeight: 110,
    },
    [theme.breakpoints.down('sm')]: {
      paddingTop: 0,
      paddingBottom: 0,
      minHeight: 0,
    },
  },
  divider: {
    height: 2,
    color: '#707070',
    marginBottom: 25,
  },
}));

const validationSchema = yup.object({
  necessity_1: yup.string('Enter a value').required('This field is required'),
  necessity_2: yup.string('Enter a value').required('This field is required'),
  waste_and_excess_1: yup.string('Enter a value').required('This field is required'),
  waste_and_excess_2: yup.string('Enter a value').required('This field is required'),
  deception_1: yup.string('Enter a value').required('This field is required'),
  deception_2: yup.string('Enter a value').required('This field is required'),
  productivity_and_balance_1: yup.string('Enter a value').required('This field is required'),
  productivity_and_balance_2: yup.string('Enter a value').required('This field is required'),
});

const Survey = () => {
  const classes = useStyles();

  /*
  {
    necessity_1: '1',
    necessity_2: '6',
    waste_and_excess_1: '3',
    waste_and_excess_2: '1',
    deception_1: '1',
    deception_2: '2',
    productivity_and_balance_1: '3',
    productivity_and_balance_2: '3',
  }
  */

  const [answers, setAnswers] = useState();

  const formik = useFormik({
    initialValues: {
      necessity_1: '',
      necessity_2: '',
      waste_and_excess_1: '',
      waste_and_excess_2: '',
      deception_1: '',
      deception_2: '',
      productivity_and_balance_1: '',
      productivity_and_balance_2: '',
    },
    validationSchema,
    onSubmit: (values) => {
      setAnswers(values);
    },
  });

  if (answers) {
    window.scrollTo(0, 0);
    return <Results results={answers} />;
  }

  return (
    <Container maxWidth="xl">
      <form onSubmit={formik.handleSubmit}>
        <Box py={1} px={2}>
          <Typography className={classes.pageTitle}>QUICK ASSESSMENT</Typography>
          <Divider className={classes.divider} />
          <Typography className={classes.pageSubhead}>
            Complete the easy questionnaire below for a quick picture of the amount of time and
            energy you spend in each of the time quadrants.
          </Typography>
          <Grid container spacing={2}>
            <Grid item md={6} sm={12} xs={12}>
              <Grid container direction="row" justify="center" alignItems="center" spacing={2}>
                <Grid item md={8} sm={12} xs={12}>
                  <Typography className={classes.pageCopy}>
                    I spend much of my time on important activities that demand my immediate
                    attention such as crises, pressing problems, and deadline-driven projects.
                  </Typography>
                </Grid>
                <Grid item md={4} sm={12} xs={12}>
                  <FormControl className={classes.margin} variant="outlined" fullWidth>
                    <Select
                      native
                      id="necessity_1"
                      name="necessity_1"
                      value={formik.values.necessity_1}
                      onChange={formik.handleChange}
                      error={formik.touched.necessity_1 && Boolean(formik.errors.necessity_1)}
                      input={<CustomInput />}
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      <option value={1}>1 Strongly Disagree</option>
                      <option value={2}>2 Disagree</option>
                      <option value={3}>3 Slightly Disagree</option>
                      <option value={4}>4 Slightly Agree</option>
                      <option value={5}>5 Agree</option>
                      <option value={6}>6 Strongly Agree</option>
                    </Select>
                    <FormHelperText
                      error={formik.touched.necessity_1 && Boolean(formik.errors.necessity_1)}
                    >
                      {formik.touched.necessity_1 && formik.errors.necessity_1}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Divider className={classes.divider} />
                </Grid>
                <Grid item md={8} sm={12} xs={12}>
                  <Typography className={classes.pageCopy}>
                    I feel I am always “putting out fires” and working in a crisis mode.
                  </Typography>
                </Grid>
                <Grid item md={4} sm={12} xs={12}>
                  <FormControl className={classes.margin} variant="outlined" fullWidth>
                    <Select
                      native
                      id="necessity_2"
                      name="necessity_2"
                      value={formik.values.necessity_2}
                      onChange={formik.handleChange}
                      error={formik.touched.necessity_2 && Boolean(formik.errors.necessity_2)}
                      input={<CustomInput />}
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      <option value={1}>1 Strongly Disagree</option>
                      <option value={2}>2 Disagree</option>
                      <option value={3}>3 Slightly Disagree</option>
                      <option value={4}>4 Slightly Agree</option>
                      <option value={5}>5 Agree</option>
                      <option value={6}>6 Strongly Agree</option>
                    </Select>
                    <FormHelperText
                      error={formik.touched.necessity_2 && Boolean(formik.errors.necessity_2)}
                    >
                      {formik.touched.necessity_2 && formik.errors.necessity_2}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Divider className={classes.divider} />
                </Grid>
                <Grid item md={8} sm={12} xs={12}>
                  <Typography className={classes.pageCopy}>
                    I feel I waste a lot of time.
                  </Typography>
                </Grid>
                <Grid item md={4} sm={12} xs={12}>
                  <FormControl className={classes.margin} variant="outlined" fullWidth>
                    <Select
                      native
                      id="waste_and_excess_1"
                      name="waste_and_excess_1"
                      value={formik.values.waste_and_excess_1}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.waste_and_excess_1 &&
                        Boolean(formik.errors.waste_and_excess_1)
                      }
                      input={<CustomInput />}
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      <option value={1}>1 Strongly Disagree</option>
                      <option value={2}>2 Disagree</option>
                      <option value={3}>3 Slightly Disagree</option>
                      <option value={4}>4 Slightly Agree</option>
                      <option value={5}>5 Agree</option>
                      <option value={6}>6 Strongly Agree</option>
                    </Select>
                    <FormHelperText
                      error={
                        formik.touched.waste_and_excess_1 &&
                        Boolean(formik.errors.waste_and_excess_1)
                      }
                    >
                      {formik.touched.waste_and_excess_1 && formik.errors.waste_and_excess_1}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Divider className={classes.divider} />
                </Grid>
                <Grid item md={8} sm={12} xs={12}>
                  <Typography className={classes.pageCopy}>
                    I spend much of my time on activities that have little relevance to my top
                    priorities, but demand my immediate attention (e.g., needless interruptions,
                    unimportant emails).
                  </Typography>
                </Grid>
                <Grid item md={4} sm={12} xs={12}>
                  <FormControl className={classes.margin} variant="outlined" fullWidth>
                    <Select
                      native
                      id="deception_1"
                      name="deception_1"
                      value={formik.values.deception_1}
                      onChange={formik.handleChange}
                      error={formik.touched.deception_1 && Boolean(formik.errors.deception_1)}
                      input={<CustomInput />}
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      <option value={1}>1 Strongly Disagree</option>
                      <option value={2}>2 Disagree</option>
                      <option value={3}>3 Slightly Disagree</option>
                      <option value={4}>4 Slightly Agree</option>
                      <option value={5}>5 Agree</option>
                      <option value={6}>6 Strongly Agree</option>
                    </Select>
                    <FormHelperText
                      error={formik.touched.deception_1 && Boolean(formik.errors.deception_1)}
                    >
                      {formik.touched.deception_1 && formik.errors.deception_1}
                    </FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <Grid container direction="row" justify="center" alignItems="center" spacing={2}>
                <Grid item md={8} sm={12} xs={12}>
                  <Typography className={classes.pageCopy}>
                    I spend much of my time on activities that are important, but not urgent, such
                    as planning, preparation, prevention, relationship building, and self-renewal.
                  </Typography>
                </Grid>
                <Grid item md={4} sm={12} xs={12}>
                  <FormControl className={classes.margin} variant="outlined" fullWidth>
                    <Select
                      native
                      id="productivity_and_balance_1"
                      name="productivity_and_balance_1"
                      value={formik.values.productivity_and_balance_1}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.productivity_and_balance_1 &&
                        Boolean(formik.errors.productivity_and_balance_1)
                      }
                      input={<CustomInput />}
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      <option value={1}>1 Strongly Disagree</option>
                      <option value={2}>2 Disagree</option>
                      <option value={3}>3 Slightly Disagree</option>
                      <option value={4}>4 Slightly Agree</option>
                      <option value={5}>5 Agree</option>
                      <option value={6}>6 Strongly Agree</option>
                    </Select>
                    <FormHelperText
                      error={
                        formik.touched.productivity_and_balance_1 &&
                        Boolean(formik.errors.productivity_and_balance_1)
                      }
                    >
                      {formik.touched.productivity_and_balance_1 &&
                        formik.errors.productivity_and_balance_1}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Divider className={classes.divider} />
                </Grid>
                <Grid item md={8} sm={12} xs={12}>
                  <Typography className={classes.pageCopy}>
                    I spend much of my time on busywork, compulsive habits, junk mail, excessive
                    TV/internet.
                  </Typography>
                </Grid>
                <Grid item md={4} sm={12} xs={12}>
                  <FormControl className={classes.margin} variant="outlined" fullWidth>
                    <Select
                      native
                      id="waste_and_excess_2"
                      name="waste_and_excess_2"
                      value={formik.values.waste_and_excess_2}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.waste_and_excess_2 &&
                        Boolean(formik.errors.waste_and_excess_2)
                      }
                      input={<CustomInput />}
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      <option value={1}>1 Strongly Disagree</option>
                      <option value={2}>2 Disagree</option>
                      <option value={3}>3 Slightly Disagree</option>
                      <option value={4}>4 Slightly Agree</option>
                      <option value={5}>5 Agree</option>
                      <option value={6}>6 Strongly Agree</option>
                    </Select>
                    <FormHelperText
                      error={
                        formik.touched.waste_and_excess_2 &&
                        Boolean(formik.errors.waste_and_excess_2)
                      }
                    >
                      {formik.touched.waste_and_excess_2 && formik.errors.waste_and_excess_2}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Divider className={classes.divider} />
                </Grid>
                <Grid item md={8} sm={12} xs={12}>
                  <Typography className={classes.pageCopy}>
                    I feel I am on top of things because of careful preparation, planning and
                    prevention.
                  </Typography>
                </Grid>
                <Grid item md={4} sm={12} xs={12}>
                  <FormControl className={classes.margin} variant="outlined" fullWidth>
                    <Select
                      native
                      id="productivity_and_balance_2"
                      name="productivity_and_balance_2"
                      value={formik.values.productivity_and_balance_2}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.productivity_and_balance_2 &&
                        Boolean(formik.errors.productivity_and_balance_2)
                      }
                      input={<CustomInput />}
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      <option value={1}>1 Strongly Disagree</option>
                      <option value={2}>2 Disagree</option>
                      <option value={3}>3 Slightly Disagree</option>
                      <option value={4}>4 Slightly Agree</option>
                      <option value={5}>5 Agree</option>
                      <option value={6}>6 Strongly Agree</option>
                    </Select>
                    <FormHelperText
                      error={
                        formik.touched.productivity_and_balance_2 &&
                        Boolean(formik.errors.productivity_and_balance_2)
                      }
                    >
                      {formik.touched.productivity_and_balance_2 &&
                        formik.errors.productivity_and_balance_2}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Divider className={classes.divider} />
                </Grid>
                <Grid item md={8} sm={12} xs={12}>
                  <Typography className={classes.pageCopy}>
                    I feel I am constantly addressing issues that are important to others, but not
                    to me.
                  </Typography>
                </Grid>
                <Grid item md={4} sm={12} xs={12}>
                  <FormControl className={classes.margin} variant="outlined" fullWidth>
                    <Select
                      native
                      id="deception_2"
                      name="deception_2"
                      value={formik.values.deception_2}
                      onChange={formik.handleChange}
                      error={formik.touched.deception_2 && Boolean(formik.errors.deception_2)}
                      input={<CustomInput />}
                    >
                      <option value="" disabled>
                        Select
                      </option>
                      <option value={1}>1 Strongly Disagree</option>
                      <option value={2}>2 Disagree</option>
                      <option value={3}>3 Slightly Disagree</option>
                      <option value={4}>4 Slightly Agree</option>
                      <option value={5}>5 Agree</option>
                      <option value={6}>6 Strongly Agree</option>
                    </Select>
                    <FormHelperText
                      error={formik.touched.deception_2 && Boolean(formik.errors.deception_2)}
                    >
                      {formik.touched.deception_2 && formik.errors.deception_2}
                    </FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <AppBar position="fixed" color="primary" className={classes.appBar}>
          <Toolbar>
            <div className={classes.grow}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box className={classes.paper}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="secondary"
                      className={classes.nextButton}
                    >
                      CONTINUE
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </div>
          </Toolbar>
        </AppBar>
      </form>
    </Container>
  );
};

export default Survey;
